/** Cliente API leve para o server nxvolt. */
export interface AuthUser {
  id: number;
  email: string;
  displayName: string;
  role: 'admin' | 'supervisor' | 'operator' | 'viewer';
  mustChangePassword: boolean;
  modules: string[];
  garageScope: 'all' | 'restricted';
  garageIds: number[];
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({ error: 'network' }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? 'error');
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

/**
 * Resposta de /api/auth/login: pode ser sucesso completo (user autenticado) OU
 * intermediário (`requires2fa` + `challengeToken`) quando o usuário tem 2FA
 * ativado. O caller precisa distinguir os dois casos.
 */
export type LoginResponse =
  | { user: AuthUser; requires2fa?: false }
  | { requires2fa: true; challengeToken: string };

export interface TwoFactorStatus {
  enabled: boolean;
  confirmedAt: string | null;
  backupCodesRemaining: number;
}

export interface TwoFactorSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrSvg: string;
}

/**
 * Feature flag conforme retornado por GET /api/feature-flags.
 * `updatedBy` é o id do usuário que fez o último toggle (backend não expande
 * pra e-mail hoje — pode ser null se ninguém tocou desde o seed).
 * `value` é jsonb livre e não é exibido na UI: só a flag em si tem toggle.
 */
export interface FeatureFlag {
  key: string;
  enabled: boolean;
  value: unknown;
  description: string | null;
  updatedAt: string;
  updatedBy: number | null;
}

export const api = {
  login: (email: string, password: string) =>
    req<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  verifyLoginTwoFactor: (challengeToken: string, code: string) =>
    req<{ user: AuthUser }>('/api/auth/2fa-verify', {
      method: 'POST',
      body: JSON.stringify({ challengeToken, code }),
    }),
  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => req<{ user: AuthUser }>('/api/auth/me'),

  passwordRecovery: {
    status: () =>
      req<{ enabled: boolean }>('/api/auth/password-recovery/status'),
    forgot: (email: string) =>
      req<{ ok: true }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    reset: (token: string, newPassword: string) =>
      req<{ ok: true; mustLoginWith2fa: boolean }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      }),
  },

  twoFactor: {
    status: () => req<TwoFactorStatus>('/api/2fa/status'),
    setup: () =>
      req<TwoFactorSetupResponse>('/api/2fa/setup', { method: 'POST', body: '{}' }),
    verify: (secret: string, code: string) =>
      req<{ ok: true; backupCodes: string[] }>('/api/2fa/verify', {
        method: 'POST',
        body: JSON.stringify({ secret, code }),
      }),
    disable: (password: string, code: string) =>
      req<{ ok: true }>('/api/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ password, code }),
      }),
    regenerateBackupCodes: (password: string, code: string) =>
      req<{ ok: true; backupCodes: string[] }>('/api/2fa/regenerate-backup-codes', {
        method: 'POST',
        body: JSON.stringify({ password, code }),
      }),
  },

  featureFlags: {
    list: () => req<{ flags: FeatureFlag[] }>('/api/feature-flags'),
    /** Atualiza uma flag. Backend aceita `enabled` e/ou `value`; a UI só toca `enabled`. */
    patch: (key: string, patch: { enabled?: boolean; value?: unknown }) =>
      req<{ flag: { key: string; enabled: boolean; value: unknown } }>(
        `/api/feature-flags/${encodeURIComponent(key)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      ),
  },
};
